import { useEffect, useRef } from "react";

/**
 * Курсорная страница REST: общая форма ProductPage, GuestProductPage и
 * OrderPage. Тип структурный, а не ссылка на сгенерированный: примитивы
 * ниже одинаково служат всем трём, а завязка на один заставила бы приводить
 * типы на каждом втором вызове.
 */
export interface CursorPage<TItem> {
  items: TItem[];
  startCursor: string | null;
  endCursor: string | null;
  hasMore: boolean;
}

/**
 * Курсор следующей страницы либо `undefined` — сигнал TanStack, что обход
 * закончен.
 *
 * Судим по `hasMore`, а НЕ по `endCursor`: последняя страница с элементами
 * отдаёт `hasMore: false` при непустом `endCursor` (проверено на деве
 * 03.09), и условие по курсору стоило бы лишнего запроса на каждую выдачу.
 * Пустой `endCursor` при `hasMore: true` контрактом невозможен, но обход всё
 * равно останавливаем: расхождение бэка со схемой не должно превращаться в
 * бесконечный обход с `after=null`.
 */
export function nextCursor(page: CursorPage<unknown>): string | undefined {
  return page.hasMore ? (page.endCursor ?? undefined) : undefined;
}

/**
 * Параметры навигации для очередной страницы.
 *
 * Недопустимы СОЧЕТАНИЯ: `after` с `before` и `sort` с любым из них —
 * стратегия сортировки едет внутри курсора, и второй её источник рядом
 * даёт 400. Обязательного параметра навигации при этом нет: у `/products`
 * порядок по умолчанию определён (по идентификатору товара), и первая
 * страница без единого параметра — законный запрос. Не путать с
 * `GET /orders`, где ровно один из трёх ОБЯЗАТЕЛЕН: там порядок без `sort`
 * не определён.
 *
 * Поэтому первая страница получает то, что дал вызывающий (в том числе
 * ничего), а все последующие — только `after`.
 *
 * Генерик по всему объекту первой страницы, а не по одному `sort`: у
 * варианта `<TSort extends string>(p, sort?: TSort)` вывод при
 * `sort: Sort | undefined` сваливается в `string`, и результат перестаёт
 * назначаться в enum параметра запроса (проверено tsc, TS2375).
 *
 * Фильтры (`categoryId`, `search`, `pointId`, `orderType`) сюда не входят
 * намеренно: в курсор они не кладутся и шлются вызывающим на каждой
 * странице заново.
 */
export function cursorNavigation<TFirst extends object>(
  pageParam: string | null,
  firstPage: TFirst
): TFirst | { after: string } {
  return pageParam === null ? firstPage : { after: pageParam };
}

/** Минимум поверхности TanStack, который нужен дочитке. */
export interface DrainableQuery {
  hasNextPage: boolean;
  isFetching: boolean;
  isFetchNextPageError: boolean;
  fetchNextPage: () => Promise<unknown>;
  data?: { pages: readonly unknown[] };
}

/**
 * Автодочитка: тянет страницы, пока они есть.
 *
 * Нужна там, где потребителю нужен ВЕСЬ список, а не окно: клиентский поиск
 * и группировка по категориям в виджете, скан каталога ради группы варианта
 * в админке. Обход при этом остаётся курсорным — «попросить всё одним
 * запросом» сервер больше не умеет.
 *
 * Три причины остановиться, и каждая обязательна:
 *
 * - страниц больше нет (`hasNextPage`);
 * - предыдущая страница упала. `hasNextPage` считается из `data`, которая на
 *   ошибке не меняется, и остаётся `true`; `isFetching` при этом
 *   переключается — без явной ветки по `isFetchNextPageError` эффект бил бы
 *   в упавший маршрут бесконечно, а в виджете каждая неудача ещё и
 *   показывала бы снекбар;
 * - упор в `maxPages`. Это предохранитель от бэка, который вечно отвечает
 *   `hasMore: true`, а не продуктовый потолок: при `limit` 100 полсотни
 *   страниц — пять тысяч позиций, вдесятеро больше живого каталога.
 *
 * Ждём ЛЮБОГО запроса (`isFetching`), а не только догрузки: у
 * `fetchNextPage` параметр `cancelRefetch` по умолчанию `true`, и вызов
 * посреди перезапроса всех страниц после инвалидации отменил бы его,
 * оставив выгруженные страницы устаревшими.
 *
 * Возвращает `isDraining` — им потребитель гейтит спиннер. Гейтить по
 * `hasNextPage` нельзя: на ошибке и на упоре в потолок он остаётся `true`
 * навсегда, и спиннер залипает поверх готового списка, пряча блок ошибки.
 *
 * И `limitReached` — упор в потолок. Наружу он нужен затем же, зачем
 * потребитель читает `query.error`: обе причины означают неполный список, и
 * до сих пор вторая была видна только сообщением в `console`.
 */
export function useDrainAll(
  query: DrainableQuery,
  options?: { maxPages?: number; onLimitReached?: () => void }
): { isDraining: boolean; limitReached: boolean } {
  const maxPages = options?.maxPages ?? 50;
  const onLimitReached = options?.onLimitReached;
  const { hasNextPage, isFetching, isFetchNextPageError, fetchNextPage } = query;
  const loaded = query.data?.pages.length ?? 0;

  const limitReached = hasNextPage && loaded >= maxPages;
  const isDraining = hasNextPage && !isFetchNextPageError && !limitReached;

  // Ровно одно сообщение на упор. Флаг страхует от пересоздания
  // onLimitReached вызывающим: обработчик почти всегда стрелка, и стоит ей
  // приехать без useCallback — эффект перезапускался бы на каждом рендере,
  // а вместе с ним и сообщение.
  const notified = useRef(false);
  useEffect(() => {
    if (!limitReached) {
      notified.current = false;
      return;
    }
    if (notified.current) return;
    notified.current = true;
    onLimitReached?.();
  }, [limitReached, onLimitReached]);

  // `loaded` в зависимостях обязателен, и это не украшение. Без него эффект
  // сторожит только переход isFetching, а тот схлопывается: когда страница
  // приезжает быстрее, чем React успевает перерисовать, компонент не видит
  // isFetching:true вовсе — false→true→false проходит внутри одного цикла,
  // ни одна зависимость не меняется, и дочитка молча замирает на второй
  // странице. Число загруженных страниц растёт при каждом успехе всегда.
  useEffect(() => {
    if (!isDraining || isFetching) return;
    void fetchNextPage();
  }, [isDraining, isFetching, loaded, fetchNextPage]);

  // limitReached наружу: потребителю нужно отличать «показан весь список» от
  // «показан кусок». Ошибку обхода он видит по query.error, а упор в потолок
  // до сих пор не был виден ничем, кроме сообщения в console.
  return { isDraining, limitReached };
}
